import type React from 'react';
import axios from 'axios';
import { MODUL_LABELS } from '../../ModulVerwaltung';
import { BEREICHE } from '../../../lib/rechte';

export const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export const AKTIONEN = ['lesen', 'anlegen', 'aendern', 'loeschen', 'freigeben'];

const AKTION_LABELS: Record<string, string> = {
  lesen: 'Lesen',
  anlegen: 'Anlegen',
  aendern: 'Ändern',
  loeschen: 'Löschen',
  freigeben: 'Freigeben',
};

const ZUSATZ_MODUL_LABELS: Record<string, string> = {
  regiebericht: 'Regieberichte',
  dokument: 'Dokumente',
};

const FAEHIGKEIT_LABELS: Record<string, string> = {
  preise_sehen: 'Preise sehen',
  websuche: 'Websuche',
  github_tickets: 'GitHub-Tickets',
  rechte_verwalten: 'Rechte verwalten',
  belege_senden: 'Belege senden',
  zahlungen_buchen: 'Zahlungen buchen',
  lieferscheine_buchen: 'Lieferscheine buchen',
};

const WERT_LABELS: Record<string, string> = {
  KEINE: 'Keine',
  EIGENE: 'Eigene',
  ZUGEWIESENE: 'Zugewiesene',
  ALLE: 'Alle',
  NEIN: 'Nein',
  JA: 'Ja',
};

export function aktionLabel(aktion: string): string {
  return AKTION_LABELS[aktion] ?? aktion;
}

export function modulLabel(modul: string): string {
  return MODUL_LABELS[modul] ?? ZUSATZ_MODUL_LABELS[modul] ?? modul;
}

export function wertLabel(wert: string): string {
  return WERT_LABELS[wert] ?? wert;
}

/** Lesbare Bezeichnung eines Rechteschlüssels (`auftrag.lesen`, `faehigkeit.websuche`, `agent.lager`). */
export function schluesselLabel(schluessel: string): string {
  const [praefix, rest = ''] = schluessel.split('.');
  if (praefix === 'faehigkeit') return FAEHIGKEIT_LABELS[rest] ?? rest;
  if (praefix === 'agent') return `Agent ${modulLabel(rest)}`;
  return `${modulLabel(praefix)} · ${aktionLabel(rest)}`;
}

/** Erlaubte Werte eines Schlüssels: JA/NEIN für Fähigkeiten und Agenten, sonst die Bereichsstufen. */
export function werteFuer(schluessel: string): string[] {
  return schluessel.startsWith('faehigkeit.') || schluessel.startsWith('agent.')
    ? ['NEIN', 'JA']
    : BEREICHE;
}

/** Fehlertext aus einer Axios-Antwort (`detail`) oder ein Fallback. */
export function fehlerText(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail !== undefined) return JSON.stringify(detail);
    return err.message || fallback;
  }
  return fallback;
}

/** Gemeinsame Inline-Stile der Rechte-Tabs (Kontor: eckig, Mono für Kleinschrift). */
export const STIL: Record<string, React.CSSProperties> = {
  fehler: { color: 'var(--red)', border: '1px solid var(--red)', padding: '8px 12px', marginBottom: 12, fontSize: 13 },
  hinweis: { color: 'var(--ink-2)', fontSize: 13, marginBottom: 12 },
  badge: {
    display: 'inline-block', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.06em',
    textTransform: 'uppercase', border: '1px solid var(--gold)', color: 'var(--gold)',
    padding: '1px 5px', marginLeft: 6, borderRadius: 0, whiteSpace: 'nowrap',
  },
  th: { textAlign: 'left', padding: '6px 10px', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.06em' },
  td: { padding: '6px 10px', borderBottom: '1px solid var(--bone-3)', fontSize: 13 },
  select: { padding: '5px 8px', borderRadius: 0, fontSize: 13, minWidth: 110 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 },
  untertitel: { fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '22px 0 8px' },
};
